import type { FC, ReactNode } from 'react'
import type { ReviewRow } from '@/lib/cms'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, ArrowUpRight, Quote, Star } from 'lucide-react'
import { revealDelay } from '@/lib/motion'

import { BUSINESS } from '@/content/business'

interface ReviewGridProps {
    reviews: ReviewRow[]
    title?: string
    intro?: string
    tone?: 'plain' | 'tinted'
}

const ReviewGrid: FC<ReviewGridProps> = ({ reviews = [], title = "What homeowners say on Google", intro, tone = 'tinted' }): ReactNode => {
    const { pathname } = useRouter()
    const onReviewsPage = pathname === '/reviews'

    if (!reviews.length) return null

    return (
        <section aria-labelledby='reviews-heading' className={tone === 'tinted' ? 'bg-sand' : ''}>
            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
                <div className='flex flex-wrap items-end justify-between gap-6' data-reveal>
                    <div className='max-w-2xl'>
                        <h2 id='reviews-heading' className='text-3xl font-bold sm:text-4xl'>
                            {title}
                        </h2>

                        {intro && (
                            <p className='mt-3 text-lg text-muted-foreground'>
                                {intro}
                            </p>
                        )}
                    </div>

                    <div className='flex flex-wrap items-center gap-3'>
                        <a
                            href={BUSINESS.googleReviewsUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex min-h-14 items-center gap-2 rounded-xl border-2 border-primary px-5 font-bold text-primary transition-colors hover:bg-white'
                        >
                            Read all our Google reviews
                            <ArrowUpRight className='size-5' aria-hidden='true' />
                        </a>

                        {!onReviewsPage && (
                            <Link href='/reviews/' className='inline-flex min-h-14 items-center gap-2 px-2 font-bold text-primary underline underline-offset-4'>
                                More reviews
                                <ArrowRight className='size-5' aria-hidden='true' />
                            </Link>
                        )}
                    </div>
                </div>

                <ul className='mt-10 grid gap-5 md:grid-cols-2'>
                    {reviews.map((review, index) => (
                        <li
                            key={review.id}
                            className='flex flex-col rounded-3xl bg-white p-7 shadow-sm'
                            data-reveal
                            style={revealDelay((index % 2) * 120)}
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <span className='flex gap-1 text-gold' aria-hidden='true'>
                                    {Array.from({ length: 5 }).map((_, star) => (
                                        <Star key={star} className='size-5 fill-current' />
                                    ))}
                                </span>

                                <span className='text-sm font-semibold text-muted-foreground' data-mdlfy-cms={`reviews:${review.id}:source`}>{review.source}</span>
                            </div>

                            <figure className='mt-4 flex flex-1 flex-col'>
                                <Quote className='size-7 text-primary/40' aria-hidden='true' />

                                <blockquote className='mt-2 flex-1 text-xl leading-relaxed text-foreground'>
                                    <p data-mdlfy-cms={`reviews:${review.id}:quote`}>{review.quote}</p>
                                </blockquote>

                                <figcaption className='mt-5 text-lg font-bold text-navy'>
                                    <span data-mdlfy-cms={`reviews:${review.id}:reviewer_name`}>{review.reviewer_name}</span>

                                    {review.town && (
                                        <span className='font-normal text-muted-foreground'>
                                            {', '}
                                            <span data-mdlfy-cms={`reviews:${review.id}:town`}>{review.town}</span>
                                        </span>
                                    )}
                                </figcaption>
                            </figure>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default ReviewGrid
